import { useState } from "react";
import { Share, SquarePlus, MoreVertical, Download, X, Smartphone } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useInstall } from "@/hooks/useInstall";
import { Mascot } from "@/components/shared/Mascot";
import { cn } from "@/lib/utils";

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="serif text-2xl text-[var(--coral)] w-6 shrink-0 leading-none">{n}.</span>
    <span className="w-9 h-9 rounded-full bg-[var(--cream-surface)] flex items-center justify-center shrink-0"><Icon size={16} /></span>
    <p className="text-sm leading-relaxed pt-1.5">{children}</p>
  </li>
);

export const InstallGuide = ({ open, onOpenChange }) => {
  const { platform, isSafari, canPrompt, promptInstall } = useInstall();
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-[var(--cream)] border-[var(--line)] max-w-md mx-auto rounded-t-[28px]" data-testid="install-guide-sheet">
        <div className="px-5 pb-[max(env(safe-area-inset-bottom),24px)] pt-2">
          <DrawerTitle className="serif text-2xl">Instalar na tela inicial</DrawerTitle>
          <p className="text-sm text-[var(--coffee-muted)] mt-1">Sem loja, sem download. Abre como app, com ícone e tela cheia.</p>
          {platform === "ios" && (
            <ol className="mt-6 space-y-4" data-testid="install-steps-ios">
              {!isSafari && <li className="rounded-2xl bg-[var(--coral-light)] p-3 text-sm">No iPhone, a instalação só funciona pelo <strong>Safari</strong>. Copie o endereço e abra lá.</li>}
              <Step n={1} icon={Share}>Toque no botão <strong>Compartilhar</strong> (quadrado com seta pra cima, na barra do Safari).</Step>
              <Step n={2} icon={SquarePlus}>Role a lista e toque em <strong>Adicionar à Tela de Início</strong>.</Step>
              <Step n={3} icon={Smartphone}>Confirme em <strong>Adicionar</strong>. Pronto — “Nossa Base” aparece com os outros apps.</Step>
            </ol>
          )}
          {platform === "android" && (
            <ol className="mt-6 space-y-4" data-testid="install-steps-android">
              {canPrompt && <button className="btn-primary w-full h-14" onClick={() => promptInstall().then((o) => o !== "dismissed" && onOpenChange(false))} data-testid="install-guide-prompt-button"><Download size={18} /> Instalar agora</button>}
              <Step n={1} icon={MoreVertical}>Toque nos <strong>três pontinhos</strong> do Chrome (canto superior direito).</Step>
              <Step n={2} icon={Download}>Toque em <strong>Instalar app</strong> (ou “Adicionar à tela inicial”).</Step>
              <Step n={3} icon={Smartphone}>Confirme. O ícone da Nossa Base vai pra sua tela inicial.</Step>
            </ol>
          )}
          {platform === "desktop" && (
            <ol className="mt-6 space-y-4" data-testid="install-steps-desktop">
              {canPrompt && <button className="btn-primary w-full h-14" onClick={() => promptInstall().then((o) => o !== "dismissed" && onOpenChange(false))} data-testid="install-guide-prompt-button"><Download size={18} /> Instalar agora</button>}
              <Step n={1} icon={Download}>No Chrome/Edge, clique no ícone de <strong>instalar</strong> à direita da barra de endereço.</Step>
              <Step n={2} icon={Smartphone}>No celular, abra este mesmo endereço e siga os passos que aparecem aqui.</Step>
            </ol>
          )}
          <div className="flex justify-center mt-6"><Mascot pose="thinking" size={96} /></div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export const InstallBanner = ({ className }) => {
  const { standalone, dismissed, dismiss, canPrompt, promptInstall, platform } = useInstall();
  const [guide, setGuide] = useState(false);
  if (standalone || dismissed) return null;
  const primary = async () => {
    if (canPrompt) { const o = await promptInstall(); if (o === "accepted") return; }
    setGuide(true);
  };
  return (
    <>
      <div className={cn("paper p-4 flex items-center gap-3", className)} data-testid="install-banner">
        <span className="w-11 h-11 rounded-2xl bg-[var(--teal-light)] text-[var(--teal)] flex items-center justify-center shrink-0"><Smartphone size={20} /></span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">Instalar como app</p>
          <p className="text-xs text-[var(--coffee-muted)] truncate">{platform === "ios" ? "Compartilhar → Tela de Início" : "um toque, sem loja"}</p>
        </div>
        <button className="btn-primary h-10 px-3 text-xs shrink-0 whitespace-nowrap" onClick={primary} data-testid="install-banner-button">{canPrompt ? "instalar" : "como faço?"}</button>
        <button className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--coffee-muted)]" onClick={dismiss} aria-label="agora não" data-testid="install-banner-dismiss"><X size={16} /></button>
      </div>
      <InstallGuide open={guide} onOpenChange={setGuide} />
    </>
  );
};

export const InstallSection = () => {
  const { standalone, canPrompt, promptInstall } = useInstall();
  const [guide, setGuide] = useState(false);
  return (
    <section className="mt-4 paper p-5" data-testid="install-section">
      <div className="flex items-center justify-between gap-3">
        <div><p className="font-semibold text-sm">Instalar no celular</p><p className="text-xs text-[var(--coffee-muted)]">{standalone ? "Já está instalado neste aparelho." : "iPhone (Safari) e Android (Chrome), direto do navegador."}</p></div>
        {!standalone && (
          <button className="btn-secondary h-10 px-4 text-xs shrink-0" onClick={() => (canPrompt ? promptInstall() : setGuide(true))} data-testid="install-section-button">{canPrompt ? "instalar" : "ver passos"}</button>
        )}
      </div>
      <InstallGuide open={guide} onOpenChange={setGuide} />
    </section>
  );
};
